import React, { useState, useEffect } from 'react';
import './MembersPage.css';
import { FaEdit } from 'react-icons/fa';
import { FiPlusCircle } from 'react-icons/fi';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify } from '../../context/FeedbackContext';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';
import HasPermi from '../../components/HasPermi'; // 新增：导入 HasPermi 组件

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const EMPTY_ADD_FORM = { firstName: '', lastName: '', email: '', contact: '' };

function MembersPage({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const selectedStoreRef = selectedRestaurant?.reference || '';

  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [addFormErrors, setAddFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const itemsPerPage = 10;

  const handleNavClick = (item) => {
    if (onNavigate) onNavigate(getRouteFromItem(item));
  };

  // Fetch members when store or search changes
  useEffect(() => {
    if (!selectedStoreRef) return;
    const timer = setTimeout(() => {
      fetchMembers();
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(timer);
  }, [selectedStoreRef, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMembers = async () => {
    setIsLoading(true);
    try {
      const url = searchTerm
        ? buildApiUrl(`/member/search?storeRef=${selectedStoreRef}&searchTerm=${encodeURIComponent(searchTerm)}&fuzzy=true`)
        : buildApiUrl(`/member/list?storeRef=${selectedStoreRef}`);
      const res = await fetch(url, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200) {
        setMembers(data.data || []);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Failed to fetch members', err);
    } finally {
      setIsLoading(false);
    }
  };

  const paginatedMembers = members.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(members.length / itemsPerPage);

  const handleEdit = (member) => {
    setEditingMember({ ...member });
    setEditFormErrors({});
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingMember(prev => ({ ...prev, [name]: value }));
    setEditFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSaveEdit = async () => {
    const errors = {};
    if (!editingMember.firstName) errors.firstName = 'First name is required.';
    if (!editingMember.lastName) errors.lastName = 'Last name is required.';
    if (!editingMember.email) errors.email = 'Email is required.';
    else if (!isValidEmail(editingMember.email)) errors.email = 'Please enter a valid email address.';
    if (!editingMember.contact) errors.contact = 'Contact is required.';
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const res = await fetch(buildApiUrl(`/member/update`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          firstName: editingMember.firstName,
          lastName: editingMember.lastName,
          email: editingMember.email,
          contact: editingMember.contact,
          reference:editingMember.reference
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        setEditingMember(null);
        fetchMembers();
        
      } else {
        notify('Failed to update member: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      notify('Failed to update member: ' + err.message, 'error');
    }
  };

  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
    setAddFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleAddMember = async () => {
    const errors = {};
    if (!addForm.firstName) errors.firstName = 'First name is required.';
    if (!addForm.lastName) errors.lastName = 'Last name is required.';
    if (!addForm.email) errors.email = 'Email is required.';
    else if (!isValidEmail(addForm.email)) errors.email = 'Please enter a valid email address.';
    if (!addForm.contact) errors.contact = 'Contact is required.';
    setAddFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const res = await fetch(buildApiUrl(`/member/create?storeRef=${selectedStoreRef}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(addForm),
      });
      const data = await res.json();
      if (data.code === 200) {
        setShowAddModal(false);
        setAddForm(EMPTY_ADD_FORM);
        fetchMembers();
      } else {
        notify('Failed to add member: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      notify('Failed to add member: ' + err.message, 'error');
    }
  };

  const [goToPage, setGoToPage] = React.useState('');

  const renderPageButtons = () => {
    const buttons = [];
    buttons.push(
      <button key="prev" className={`pg-btn${currentPage === 1 ? ' disabled' : ''}`}
        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>Previous</button>
    );
    const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
    const end = Math.min(totalPages, start + 2);
    buttons.push(<span key="dots-start" className="pg-dots" style={{ visibility: start > 1 ? 'visible' : 'hidden' }}>...</span>);
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} className={`pg-btn${currentPage === i ? ' active' : ''}`}
          onClick={() => setCurrentPage(i)}>{i}</button>
      );
    }
    buttons.push(<span key="dots-end" className="pg-dots" style={{ visibility: end < totalPages ? 'visible' : 'hidden' }}>...</span>);
    buttons.push(
      <button key="next" className={`pg-btn${currentPage === totalPages ? ' disabled' : ''}`}
        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>Next</button>
    );
    return buttons;
  };

  const showingFrom = members.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, members.length);

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="memberspage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li key={item} className={item === 'Members' ? 'active' : ''}
              onClick={() => handleNavClick(item)}>
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="members-content">
        <div className="admin-breadcrumb">
          <span className="breadcrumb-link" onClick={() => handleNavClick('Home')}>Home</span>
          <span className="breadcrumb-sep">›</span>
          <span className="breadcrumb-current">Member Management</span>
        </div>
        <h1 className="admin-page-title">Member Management</h1>
        <p className="admin-page-subtitle">Manage all members, view their details and account information.</p>

        {/* 整个成员管理区域 - 需要 members:list 或 members:search 权限 */}
        <HasPermi permission={['members:list', 'members:search']} any>
          <div className="user-list-card">
            <div className="user-list-header">
              <h2 className="user-list-title">Member List</h2>
              <div className="user-list-actions">
                <div className="search-box">
                  <input type="text" placeholder="Search members..." value={searchTerm}
                    onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                </div>
                {/* 添加成员按钮 - 需要 members:create 权限 */}
                <HasPermi permission="members:create">
                  <button className="add-restaurant-btn" onClick={() => { setShowAddModal(true); setAddFormErrors({}); setAddForm(EMPTY_ADD_FORM); }}>
                    <FiPlusCircle /> Add Member
                  </button>
                </HasPermi>
              </div>
            </div>

            <table className="restaurant-table">
              <thead>
                <tr>
                  <th>FIRST NAME</th>
                  <th>LAST NAME</th>
                  <th>EMAIL</th>
                  <th>CONTACT</th>
                  <th>JOIN DATE</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>Loading...</td></tr>
                ) : paginatedMembers.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No members found.</td></tr>
                ) : paginatedMembers.map(member => (
                  <tr key={member.id}>
                    <td>{member.firstName}</td>
                    <td>{member.lastName}</td>
                    <td>{member.email}</td>
                    <td>{member.contact}</td>
                    <td>{member.createdAt}</td>
                    <td>
                      {/* 编辑按钮 - 需要 members:update 权限 */}
                      <HasPermi permission="members:update">
                        <button className="edit-btn" onClick={() => handleEdit(member)}><FaEdit /></button>
                      </HasPermi>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination-row">
              <span className="pagination-info">Showing {showingFrom} to {showingTo} of {members.length} results</span>
              <div className="pagination-buttons">{renderPageButtons()}</div>
              <div className="pagination-goto">
                <span>Go to page</span>
                <input className="pg-goto-input" type="number" min="1" max={totalPages} value={goToPage}
                  onChange={e => setGoToPage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } } }}
                  placeholder="" />
                <button className="pg-btn" onClick={() => { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } }}>Go</button>
              </div>
            </div>
          </div>
        </HasPermi>

        {/* 如果没有权限，显示提示信息 */}
        <HasPermi
          permission={['members:list', 'members:search']}
          any
          fallback={
            <div className="no-permission-message">
              <p>You don't have permission to view members. Please contact administrator.</p>
            </div>
          }
        >
          {/* 这个空的 HasPermi 用于显示回退内容 */}
        </HasPermi>
      </main>

      {/* Add Member Modal - 需要 members:create 权限 */}
      <HasPermi permission="members:create">
        {showAddModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={() => setShowAddModal(false)}>×</button>
                <h2 className="admin-modal-title">Add Member</h2>
                <p className="admin-modal-subtitle">Add a new member. Fill in all required fields.</p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>First Name <span className="required">*</span></label>
                  <input className={addFormErrors.firstName ? 'input-error' : ''} name="firstName" value={addForm.firstName} onChange={handleAddInputChange} placeholder="Enter first name" />
                  {addFormErrors.firstName && <p className="field-error">{addFormErrors.firstName}</p>}
                </div>
                <div className="form-field">
                  <label>Last Name <span className="required">*</span></label>
                  <input className={addFormErrors.lastName ? 'input-error' : ''} name="lastName" value={addForm.lastName} onChange={handleAddInputChange} placeholder="Enter last name" />
                  {addFormErrors.lastName && <p className="field-error">{addFormErrors.lastName}</p>}
                </div>
                <div className="form-field">
                  <label>Email <span className="required">*</span></label>
                  <input className={addFormErrors.email ? 'input-error' : ''} name="email" type="email" value={addForm.email} onChange={handleAddInputChange} placeholder="Enter email address" />
                  {addFormErrors.email && <p className="field-error">{addFormErrors.email}</p>}
                </div>
                <div className="form-field">
                  <label>Contact <span className="required">*</span></label>
                  <input className={addFormErrors.contact ? 'input-error' : ''} name="contact" value={addForm.contact} onChange={handleAddInputChange} placeholder="e.g. +65 9123-4567" />
                  {addFormErrors.contact && <p className="field-error">{addFormErrors.contact}</p>}
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="submit-btn" onClick={handleAddMember}>Add Member</button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>

      {/* Edit Member Modal - 需要 members:update 权限 */}
      <HasPermi permission="members:update">
        {editingMember && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={() => setEditingMember(null)}>×</button>
                <h2 className="admin-modal-title">Edit Member</h2>
                <p className="admin-modal-subtitle">Update the member's information.</p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>First Name <span className="required">*</span></label>
                  <input className={editFormErrors.firstName ? 'input-error' : ''} name="firstName" value={editingMember.firstName || ''} onChange={handleEditInputChange} placeholder="Enter first name" />
                  {editFormErrors.firstName && <p className="field-error">{editFormErrors.firstName}</p>}
                </div>
                <div className="form-field">
                  <label>Last Name <span className="required">*</span></label>
                  <input className={editFormErrors.lastName ? 'input-error' : ''} name="lastName" value={editingMember.lastName || ''} onChange={handleEditInputChange} placeholder="Enter last name" />
                  {editFormErrors.lastName && <p className="field-error">{editFormErrors.lastName}</p>}
                </div>
                <div className="form-field">
                  <label>Email <span className="required">*</span></label>
                  <input className={editFormErrors.email ? 'input-error' : ''} name="email" type="email" value={editingMember.email || ''} onChange={handleEditInputChange} placeholder="Enter email address" />
                  {editFormErrors.email && <p className="field-error">{editFormErrors.email}</p>}
                </div>
                <div className="form-field">
                  <label>Contact <span className="required">*</span></label>
                  <input className={editFormErrors.contact ? 'input-error' : ''} name="contact" value={editingMember.contact || ''} onChange={handleEditInputChange} placeholder="e.g. +65 9123-4567" />
                  {editFormErrors.contact && <p className="field-error">{editFormErrors.contact}</p>}
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={() => setEditingMember(null)}>Cancel</button>
                <button className="submit-btn" onClick={handleSaveEdit}>Save Changes</button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>
    </div>
  );
}

export default MembersPage;
